import React, { useState, useEffect, useCallback, useRef } from 'react';
import LexicalEditor from './LexicalEditor';
import LexicalViewer from './LexicalViewer';
import RelatedInformation from './RelatedInformation';
import { 
  getDimensions, 
  getTagsByDimension, 
  getFeatureTags
} from '../services/featureService';
import { getReleases, getFeatureReleases } from '../services/releaseService';
import { logger } from '../utils/logger';
import { sanitizeInput, sanitizeURL } from '../utils/sanitize';
import { DIMENSION_TAG_LIST_CHANGED } from '../utils/dimensionTagSync';
import { supabase } from '../lib/supabase';
import './FeaturePanel.css';

const FeaturePanel = ({ isOpen, mode, feature, parentFeature, allFeatures, featureGroupId, onSave, onSaveAndClose, onSaveAndCopy, onClose, onEdit, onCancelToView, userRole = null, userId = null, userProfile = null, onImprovePrompt = null, onImprovePromptStart = null, inline = false }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [details, setDetails] = useState('');
  const [status, setStatus] = useState('No Status');
  const [parentId, setParentId] = useState('');
  const [sortOrder, setSortOrder] = useState(null);
  // Dimension-based tag state
  const [dimensions, setDimensions] = useState([]);
  const [visibleDimensions, setVisibleDimensions] = useState([]);
  const [featureTagAssignments, setFeatureTagAssignments] = useState(new Map()); // Map<dimensionId, Map<tagId, {state, rating, comment}>>
  const [selectedDimensions, setSelectedDimensions] = useState(new Set()); // Dimensions shown in UI
  const [dimensionTags, setDimensionTags] = useState(new Map()); // Map<dimensionId, tags[]>
  const [showAddDimensionMenu, setShowAddDimensionMenu] = useState(false);
  const [editingComments, setEditingComments] = useState(new Map()); // Map<"dimensionId-tagId", commentText>
  const [commentInputs, setCommentInputs] = useState(new Map()); // Map<"dimensionId-tagId", commentText>
  const [selectedReleaseId, setSelectedReleaseId] = useState('');
  const [availableReleases, setAvailableReleases] = useState([]);
  const [featureReleases, setFeatureReleasesState] = useState([]);
  const [errors, setErrors] = useState({});
  const [isEditMode, setIsEditMode] = useState(false);
  const [parentSearchQuery, setParentSearchQuery] = useState('');
  const [showParentDropdown, setShowParentDropdown] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const [aiPrompt, setAiPrompt] = useState('');
  const [privateFeature, setPrivateFeature] = useState(false);
  const [initialDetails, setInitialDetails] = useState('');
  const [initialAiPrompt, setInitialAiPrompt] = useState('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showOtherOptions, setShowOtherOptions] = useState(false);
  /** null | 'continue' | 'close' | 'copy' — shows spinner on the matching save control */
  const [savingState, setSavingState] = useState(null);
  const [originalUpdatedAt, setOriginalUpdatedAt] = useState(null); // For optimistic locking
  const [conflictError, setConflictError] = useState(null); // For conflict detection UI
  const [editingUsers, setEditingUsers] = useState(new Set()); // Set of user IDs currently viewing/editing
  const [presenceUserMap, setPresenceUserMap] = useState(new Map()); // Map<user_id, {name, email, avatar_url, first_name, last_name}>
  const [presenceUserModes, setPresenceUserModes] = useState(new Map()); // Map<user_id, 'view' | 'edit'> - tracks mode for each user
  const [hoveredPresenceUserId, setHoveredPresenceUserId] = useState(null); // Track which avatar is being hovered
  const panelContentRef = useRef(null);

  const STATUS_OPTIONS = ['Live', 'Testing', 'In development', 'Planning', 'Requested', 'Won\'t do', 'No Status'];

  const saveBusy = savingState !== null;

  const getParentName = useCallback((parentId) => {
    if (!parentId) return 'None (Top-level feature)';
    const findParent = (features, targetId) => {
      for (const f of features) {
        if (f.id === targetId) return f.name;
        if (f.subFeatures && f.subFeatures.length > 0) {
          const found = findParent(f.subFeatures, targetId);
          if (found) return found;
        }
      }
      return null;
    };
    const parentName = findParent(allFeatures, parentId);
    return parentName || `Unknown (${parentId.substring(0, 8)}...)`;
  }, [allFeatures]);

  // Define loadFeatureReleases and loadFeatureTags before the useEffect that uses them
  const loadFeatureReleases = useCallback(async () => {
    if (!feature?.id) return;
    try {
      const releases = await getFeatureReleases(feature.id);
      setFeatureReleasesState(releases);
      // Set the first release ID, or empty string if no releases
      setSelectedReleaseId(releases.length > 0 ? releases[0].id : '');
    } catch (err) {
      logger.error('Error loading feature releases:', err);
    }
  }, [feature?.id]);

  const loadTagsForDimension = useCallback(async (dimensionId) => {
    try {
      const tags = await getTagsByDimension(dimensionId);
      setDimensionTags(prev => {
        const newMap = new Map(prev);
        newMap.set(dimensionId, tags);
        return newMap;
      });
    } catch (err) {
      logger.error(`Error loading tags for dimension ${dimensionId}:`, err);
    }
  }, []);

  const loadFeatureTags = useCallback(async () => {
    if (!feature?.id) return;
    try {
      const tags = await getFeatureTags(feature.id);
      
      // Group tags by dimension
      const assignments = new Map();
      const selectedDims = new Set();
      
      for (const tag of tags) {
        if (tag.dimension?.id) {
          const dimId = tag.dimension.id;
          selectedDims.add(dimId);
          
          if (!assignments.has(dimId)) {
            assignments.set(dimId, new Map());
          }
          
          // For BOOLEAN dimensions, convert old 'YES' to 'TRUE' and ensure state exists
          let state = tag.state || null;
          if (tag.dimension.dimension_type === 'BOOLEAN') {
            if (state === 'YES' || state === null) {
              state = 'TRUE'; // Migrate old 'YES' or null to 'TRUE'
            }
            // If state is 'FALSE', keep it as is
          }
          
          assignments.get(dimId).set(tag.id, {
            state: state,
            rating: tag.rating || null,
            comment: tag.comment || null
          });
        }
      }
      
      setFeatureTagAssignments(assignments);
      setSelectedDimensions(selectedDims);
      
      // Reload tags for all selected dimensions to ensure we have the latest tag list
      for (const dimId of selectedDims) {
        await loadTagsForDimension(dimId);
      }
    } catch (err) {
      logger.error('Error loading feature tags:', err);
    }
  }, [feature?.id, loadTagsForDimension]);

  const loadAvailableReleases = useCallback(async () => {
    if (!featureGroupId) return;
    try {
      const releases = await getReleases(featureGroupId);
      setAvailableReleases(releases);
    } catch (err) {
      logger.error('Error loading releases:', err);
    }
  }, [featureGroupId]);

  const loadDimensions = useCallback(async () => {
    if (!featureGroupId) return;
    try {
      const allDimensions = await getDimensions(featureGroupId);
      setDimensions(allDimensions);
      
      // Filter by visibility based on user role
      const filtered = allDimensions.filter(dim => {
        if (userRole === 'Guest') return dim.visible_to_guest;
        if (userRole === 'Editor') return dim.visible_to_editor;
        if (userRole === 'Owner') return dim.visible_to_owner;
        return false;
      });
      
      setVisibleDimensions(filtered);
      
      // Load tags for all visible dimensions
      for (const dim of filtered) {
        await loadTagsForDimension(dim.id);
      }
    } catch (err) {
      logger.error('Error loading dimensions:', err);
    }
  }, [featureGroupId, userRole, loadTagsForDimension]);

  useEffect(() => {
    // Only process if panel is open
    if (!isOpen) return;
    
    if (feature && mode !== 'add') {
      setName(feature.name || '');
      setDescription(feature.description || '');
      const featureDetails = feature.details || '';
      const featurePrompt = feature.ai_prompt || '';
      setDetails(featureDetails);
      setStatus(feature.status || 'No Status');
      setParentId(feature.parent_id || '');
      setParentSearchQuery(feature.parent_id ? getParentName(feature.parent_id) : '');
      setSortOrder(feature.sort_order !== null && feature.sort_order !== undefined ? feature.sort_order : null);
      setImageUrl(feature.image_url || '');
      setAiPrompt(featurePrompt);
      setPrivateFeature(feature.private_feature || false);
      setInitialDetails(featureDetails);
      setInitialAiPrompt(featurePrompt);
      setHasUnsavedChanges(false);
      setOriginalUpdatedAt(feature.updated_at || null); // Store for optimistic locking
      setConflictError(null); // Clear any previous conflicts
      loadFeatureTags();
      loadFeatureReleases();
    } else {
      // Clear all form fields when in add mode or when feature is null
      setName('');
      setDescription('');
      setDetails('');
      setStatus('No Status');
      setParentId(parentFeature ? parentFeature.id : '');
      setParentSearchQuery(parentFeature ? parentFeature.name || '' : '');
      setSortOrder(null);
      setImageUrl('');
      setAiPrompt('');
      setPrivateFeature(false);
      setInitialDetails('');
      setInitialAiPrompt('');
      setHasUnsavedChanges(false);
      setFeatureTagAssignments(new Map());
      setSelectedDimensions(new Set());
      setSelectedReleaseId('');
      setFeatureReleasesState([]);
    }
      setShowAddDimensionMenu(false);
      setShowParentDropdown(false);
      setEditingComments(new Map());
      setCommentInputs(new Map());
      setErrors({});
      setIsEditMode(mode === 'edit' || mode === 'add');
  }, [feature, parentFeature, mode, isOpen, getParentName, loadFeatureReleases, loadFeatureTags]);

  useEffect(() => {
    if (featureGroupId) {
      loadAvailableReleases();
    }
  }, [featureGroupId, loadAvailableReleases]);

  // Load dimensions when the panel is open and the map or role changes (fresh list when opening edit)
  useEffect(() => {
    if (featureGroupId && isOpen) {
      loadDimensions();
    }
  }, [featureGroupId, userRole, loadDimensions, isOpen]);

  // Refresh dimension/tag catalog when Tags or Tag Dimensions admin changes data for this map
  useEffect(() => {
    const onDimsTagsChanged = (e) => {
      const fg = e.detail?.featureGroupId;
      if (!fg || fg !== featureGroupId || !isOpen) return;
      loadDimensions();
      if (feature?.id && mode !== 'add') {
        loadFeatureTags();
      }
    };
    window.addEventListener(DIMENSION_TAG_LIST_CHANGED, onDimsTagsChanged);
    return () => window.removeEventListener(DIMENSION_TAG_LIST_CHANGED, onDimsTagsChanged);
  }, [featureGroupId, isOpen, loadDimensions, loadFeatureTags, feature?.id, mode]);

  useEffect(() => {
    if (featureReleases.length > 0) {
      setSelectedReleaseId(featureReleases[0].id);
    } else {
      setSelectedReleaseId('');
    }
  }, [featureReleases]);

  const flattenFeatures = React.useCallback((features, excludeId = null, level = 0) => {
    let result = [];
    if (!features || !Array.isArray(features)) {
      return result;
    }
    features.forEach(f => {
      if (f.id !== excludeId) {
        result.push({
          ...f,
          level,
          displayName: '  '.repeat(level) + f.name
        });
        if (f.subFeatures && f.subFeatures.length > 0) {
          result = result.concat(flattenFeatures(f.subFeatures, excludeId, level + 1));
        }
      }
    });
    return result;
  }, []);

  // Get siblings at the same level (same parent_id)
  // When editing, include the current feature so we can show its position
  const getSiblingsAtLevel = (currentParentId, excludeId = null) => {
    const findSiblings = (features, targetParentId) => {
      let siblings = [];
      features.forEach(f => {
        const fParentId = f.parent_id || null;
        if (fParentId === targetParentId) {
          // When editing, include the current feature even if it matches excludeId
          // When adding, exclude the feature if it matches excludeId
          const shouldExclude = excludeId && f.id === excludeId && mode !== 'edit';
          if (!shouldExclude) {
            siblings.push(f);
          }
        }
        if (f.subFeatures && f.subFeatures.length > 0) {
          siblings = siblings.concat(findSiblings(f.subFeatures, targetParentId));
        }
      });
      return siblings;
    };
    
    const siblings = findSiblings(allFeatures, currentParentId || null);
    // Sort by sort_order, then created_at
    return siblings.sort((a, b) => {
      const aSort = a.sort_order || 0;
      const bSort = b.sort_order || 0;
      if (aSort !== bSort) {
        return aSort - bSort;
      }
      const aDate = new Date(a.created_at || 0);
      const bDate = new Date(b.created_at || 0);
      return aDate - bDate;
    });
  };

  // Get sort order options for dropdown
  const getSortOrderOptions = () => {
    const siblings = getSiblingsAtLevel(parentId || null, feature?.id || null);
    const options = [];
    const currentSortOrder = feature?.sort_order !== null && feature?.sort_order !== undefined ? feature.sort_order : null;
    
    // Add options for each sibling position
    siblings.forEach((sibling) => {
      const displayName = sibling.name.length > 30 
        ? sibling.name.substring(0, 30) + '...'
        : sibling.name;
      const status = sibling.status || 'No Status';
      const sortOrder = sibling.sort_order || 0;
      
      // If editing and this is the current feature, show its current position
      if (feature && mode === 'edit' && sibling.id === feature.id) {
        options.push({
          value: sortOrder,
          label: `${sortOrder} - ${displayName} [${status}] (current position)`
        });
      } else {
        // Otherwise show "Before # - (name) [status]"
        options.push({
          value: sortOrder,
          label: `Before ${sortOrder} - ${displayName} [${status}]`
        });
      }
    });
    
    // Add option for "End" (max + 1)
    const maxSortOrder = siblings.length > 0 
      ? Math.max(...siblings.map(s => s.sort_order || 0))
      : -1;
    const endValue = maxSortOrder + 1;
    
    // Add "End" option - always available for add mode, or if not current position in edit mode
    if (mode === 'add' || !feature || currentSortOrder === null || endValue !== currentSortOrder) {
      options.push({
        value: endValue,
        label: `${endValue} - End (after all features)`
      });
    }
    
    return options;
  };

  const scrollToTop = () => {
    // Scroll to top of panel content
    if (panelContentRef.current) {
      const scrollContainer = panelContentRef.current.querySelector('.panel-body') || 
                              panelContentRef.current.querySelector('.panel-content') ||
                              panelContentRef.current.querySelector('.panel-content-inline');
      if (scrollContainer) {
        scrollContainer.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        // Fallback: scroll the ref element itself
        panelContentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!name.trim()) {
      newErrors.name = 'Name is required';
    }
    // Description is required and must be plain text, max 250 characters
    if (!description.trim()) {
      newErrors.description = 'Description is required';
    } else if (description.trim().length > 250) {
      newErrors.description = 'Description must be 250 characters or less';
    }
    // Details is optional (no validation needed)
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      // Scroll to top after a brief delay to ensure error state is rendered
      setTimeout(() => scrollToTop(), 100);
    }
    return Object.keys(newErrors).length === 0;
  };

  const buildFeaturePayload = () => {
    const featureData = {
      name: sanitizeInput(name.trim()),
      description: sanitizeInput(description.trim()),
      details: details || null, // Details is optional - Lexical editor handles sanitization
      status: status || 'No Status',
      parent_id: parentId || null,
      sort_order: sortOrder !== null && sortOrder !== undefined ? parseInt(sortOrder) : undefined,
      image_url: sanitizeURL(imageUrl.trim()) || null,
      ai_prompt: sanitizeInput(aiPrompt.trim()) || null,
      private_feature: privateFeature,
      originalUpdatedAt: originalUpdatedAt // Include for optimistic locking
    };
    const releaseIds = selectedReleaseId ? [selectedReleaseId] : [];
    return { featureData, releaseIds };
  };

  const performSave = async (saveAndClose = false) => {
    if (!validate()) return;
    const { featureData, releaseIds } = buildFeaturePayload();

    setSavingState(saveAndClose ? 'close' : 'continue');
    try {
      // Clear any previous conflict errors
      setConflictError(null);

      const tagAssignments = getTagAssignmentsForSave();
      if (saveAndClose && onSaveAndClose) {
        await onSaveAndClose(featureData, tagAssignments, releaseIds);
      } else {
        // Regular save - should switch to view mode after saving
        await onSave(featureData, tagAssignments, releaseIds);
        // Note: handleSaveFeature in App.js handles switching to view mode
        // No need to call onCancelToView here as it's handled by the parent
      }

      // After a successful save, reset initial values so we don't warn
      setInitialDetails(featureData.details || '');
      setInitialAiPrompt(featureData.ai_prompt || '');
      setHasUnsavedChanges(false);

      // Reload feature tags if in edit mode
      if (mode === 'edit' && feature?.id) {
        await loadFeatureTags();
      }
    } catch (err) {
      // Handle conflict errors
      if (err.message === 'FEATURE_CONFLICT' || err.message?.includes('modified by another user')) {
        setConflictError('This feature was modified by another user. The latest version has been loaded. Please review the changes and try saving again.');
        logger.warn('Conflict detected:', err);
      } else {
        logger.error('Error saving feature:', err);
        setConflictError('Failed to save feature. Please try again.');
      }
      // Scroll to top to show error message
      setTimeout(() => scrollToTop(), 100);
      // Re-throw to allow parent to handle if needed
      throw err;
    } finally {
      setSavingState(null);
    }
  };

  const handleSubmit = async (e, saveAndClose = false) => {
    e.preventDefault();
    await performSave(saveAndClose);
  };

  const performSaveAndCopy = async () => {
    if (!onSaveAndCopy || !validate()) return;
    const { featureData, releaseIds } = buildFeaturePayload();
    const tagAssignments = getTagAssignmentsForSave();
    setSavingState('copy');
    try {
      await onSaveAndCopy(featureData, tagAssignments, releaseIds);
      // After save & copy, current content is considered saved
      setInitialDetails(featureData.details || '');
      setInitialAiPrompt(featureData.ai_prompt || '');
      setHasUnsavedChanges(false);
    } catch (err) {
      logger.error('Error saving and copying feature:', err);
      setConflictError('Failed to save and duplicate. Please try again.');
      setTimeout(() => scrollToTop(), 100);
      throw err;
    } finally {
      setSavingState(null);
    }
  };


  const handleEditClick = () => {
    setIsEditMode(true);
    if (onEdit) {
      onEdit();
    }
  };

  const confirmDiscardChanges = () => {
    if (!hasUnsavedChanges) return true;
    return window.confirm(
      'You have unsaved changes in the Details or AI Prompt fields. Do you want to discard your changes and continue?'
    );
  };

  const handleCancel = () => {
    if (!confirmDiscardChanges()) {
      return;
    }

    if (mode === 'add') {
      onClose();
    } else {
      setIsEditMode(false);
      // Reset form to original values
      if (feature) {
        setName(feature.name || '');
        setDescription(feature.description || '');
        setParentId(feature.parent_id || '');
        setParentSearchQuery(feature.parent_id ? getParentName(feature.parent_id) : '');
        setSortOrder(feature.sort_order !== null && feature.sort_order !== undefined ? feature.sort_order : null);
        loadFeatureTags();
      }
      setShowAddDimensionMenu(false);
      setErrors({});
      // Notify parent to switch back to view mode
      if (onCancelToView) {
        onCancelToView();
      }
    }
  };

  const handleParentSearchChange = (e) => {
    const value = e.target.value;
    setParentSearchQuery(value);
    setShowParentDropdown(true);
  };

  const getFilteredParents = () => {
    if (!parentSearchQuery.trim()) return availableParents;
    const queryLower = parentSearchQuery.toLowerCase();
    return availableParents.filter(parent =>
      parent.name.toLowerCase().includes(queryLower)
    );
  };

  const handleSelectParent = (parent) => {
    setParentId(parent.id);
    setParentSearchQuery(parent.name || '');
    setSortOrder(null); // Reset sort order when parent changes
    setShowParentDropdown(false);
  };

  const handleClearParent = () => {
    setParentId('');
    setParentSearchQuery('');
    setSortOrder(null);
    setShowParentDropdown(false);
  };

  // Helper to check if user can edit tags in a dimension
  const canEditDimensionTags = (dimension) => {
    if (!dimension) return false;
    if (userRole === 'Owner') return dimension.editable_to_owner ?? true;
    if (userRole === 'Editor') return dimension.editable_to_editor ?? true;
    if (userRole === 'Guest') return dimension.editable_to_guest ?? false;
    return false;
  };

  // Helper to get available dimensions for plus menu (not already shown)
  const getAvailableDimensionsForPlus = () => {
    return visibleDimensions.filter(dim => !selectedDimensions.has(dim.id));
  };

  // Add dimension to selected dimensions
  const handleAddDimension = (dimension) => {
    setSelectedDimensions(prev => new Set([...prev, dimension.id]));
    setShowAddDimensionMenu(false);
    // Load tags if not already loaded
    if (!dimensionTags.has(dimension.id)) {
      loadTagsForDimension(dimension.id);
    }
  };

  // Remove dimension section (warn if tags exist)
  const handleRemoveDimension = (dimensionId) => {
    const assignments = featureTagAssignments.get(dimensionId);
    const hasTags = assignments && assignments.size > 0;
    
    if (hasTags) {
      const confirmMsg = `This dimension has ${assignments.size} tag(s) assigned. Removing it will delete these tag assignments. Continue?`;
      if (!window.confirm(confirmMsg)) return;
    }
    
    setSelectedDimensions(prev => {
      const newSet = new Set(prev);
      newSet.delete(dimensionId);
      return newSet;
    });
    
    // Remove tag assignments for this dimension
    setFeatureTagAssignments(prev => {
      const newMap = new Map(prev);
      newMap.delete(dimensionId);
      return newMap;
    });
  };

  // Handle tag selection/assignment for a dimension
  const handleTagToggle = (dimensionId, tagId, dimensionType) => {
    setFeatureTagAssignments(prev => {
      const newMap = new Map(prev);
      if (!newMap.has(dimensionId)) {
        newMap.set(dimensionId, new Map());
      }
      const dimAssignments = newMap.get(dimensionId);
      
      if (dimensionType === 'BOOLEAN') {
        // For BOOLEAN, toggle between TRUE and FALSE (always keep the assignment)
        if (dimAssignments.has(tagId)) {
          const current = dimAssignments.get(tagId);
          // Toggle: TRUE -> FALSE, FALSE -> TRUE
          dimAssignments.set(tagId, {
            state: current.state === 'TRUE' ? 'FALSE' : 'TRUE',
            rating: null,
            comment: current.comment || null
          });
        } else {
          // New assignment: default to TRUE
          dimAssignments.set(tagId, {
            state: 'TRUE',
            rating: null,
            comment: null
          });
        }
      } else {
        // For TRI_STATE and RATING, remove/add the assignment
        if (dimAssignments.has(tagId)) {
          dimAssignments.delete(tagId);
        } else {
          // Add tag with default state based on dimension type
          let defaultState = null;
          let defaultRating = null;
          
          if (dimensionType === 'TRI_STATE') {
            defaultState = 'UNSET'; // Start with UNSET
          } else if (dimensionType === 'RATING') {
            defaultRating = null; // Start with no rating
          }
          
          dimAssignments.set(tagId, {
            state: defaultState,
            rating: defaultRating,
            comment: null
          });
        }
      }
      
      return newMap;
    });
  };

  // Update tag assignment (state, rating, comment)
  const handleTagAssignmentUpdate = (dimensionId, tagId, updates) => {
    setFeatureTagAssignments(prev => {
      const newMap = new Map(prev);
      if (!newMap.has(dimensionId)) {
        newMap.set(dimensionId, new Map());
      }
      const dimAssignments = newMap.get(dimensionId);
      
      if (dimAssignments.has(tagId)) {
        const current = dimAssignments.get(tagId);
        dimAssignments.set(tagId, { ...current, ...updates });
      }
      
      return newMap;
    });
  };

  // Comment editing handlers
  const handleStartEditComment = (dimensionId, tagId, currentComment) => {
    const key = `${dimensionId}-${tagId}`;
    setEditingComments(prev => new Map(prev).set(key, true));
    setCommentInputs(prev => new Map(prev).set(key, currentComment || ''));
  };

  const handleCancelEditComment = (dimensionId, tagId) => {
    const key = `${dimensionId}-${tagId}`;
    setEditingComments(prev => {
      const newMap = new Map(prev);
      newMap.delete(key);
      return newMap;
    });
    setCommentInputs(prev => {
      const newMap = new Map(prev);
      newMap.delete(key);
      return newMap;
    });
  };

  const handleSaveComment = (dimensionId, tagId) => {
    const key = `${dimensionId}-${tagId}`;
    const commentText = commentInputs.get(key) || '';
    handleTagAssignmentUpdate(dimensionId, tagId, { comment: commentText.trim() || null });
    handleCancelEditComment(dimensionId, tagId);
  };

  const handleDeleteComment = (dimensionId, tagId) => {
    if (window.confirm('Delete this comment?')) {
      handleTagAssignmentUpdate(dimensionId, tagId, { comment: null });
    }
  };

  // Convert dimension assignments to format for saving
  const getTagAssignmentsForSave = () => {
    const assignments = [];
    for (const [dimensionId, tagMap] of featureTagAssignments.entries()) {
      // Find the dimension to check its type
      const dimension = dimensions.find(d => d.id === dimensionId);
      
      for (const [tagId, assignment] of tagMap.entries()) {
        // For BOOLEAN, always include the assignment with TRUE or FALSE state
        // For other types, only include if assignment exists (not removed)
        if (dimension?.dimension_type === 'BOOLEAN') {
          // BOOLEAN always has a state (TRUE or FALSE)
          assignments.push({
            tag_id: tagId,
            state: assignment.state || 'FALSE', // Default to FALSE if somehow missing
            rating: null,
            comment: assignment.comment || null
          });
        } else {
          // TRI_STATE and RATING: only include if assignment exists
          assignments.push({
            tag_id: tagId,
            state: assignment.state || null,
            rating: assignment.rating || null,
            comment: assignment.comment || null
          });
        }
      }
    }
    return assignments;
  };

  const getStatusClass = (status) => {
    const statusMap = {
      'Live': 'status-live',
      'Testing': 'status-testing',
      'In development': 'status-in-development',
      'Planning': 'status-planning',
      'Requested': 'status-requested',
      'Won\'t do': 'status-wont-do',
      'No Status': 'status-no-status'
    };
    return statusMap[status] || 'status-no-status';
  };

  const availableParents = feature 
    ? flattenFeatures(allFeatures, feature.id)
    : flattenFeatures(allFeatures);

  // Track unsaved changes when Details or AI Prompt differ from initial values
  useEffect(() => {
    const detailsChanged = (details || '') !== (initialDetails || '');
    const promptChanged = (aiPrompt || '') !== (initialAiPrompt || '');
    const dirty = (detailsChanged || promptChanged) && (isEditMode || mode === 'add');
    setHasUnsavedChanges(dirty);
  }, [details, aiPrompt, initialDetails, initialAiPrompt, isEditMode, mode]);

  // Warn on browser back/refresh/tab close if there are unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (!hasUnsavedChanges) return;
      event.preventDefault();
      event.returnValue = '';
    };

    if (hasUnsavedChanges) {
      window.addEventListener('beforeunload', handleBeforeUnload);
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  // Generate a unique session ID for this browser tab/window (persists for component lifetime)
  // This allows showing one icon per browser session, not just per user
  const sessionIdRef = useRef(null);
  if (!sessionIdRef.current && userId) {
    sessionIdRef.current = `${userId}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  // Track user presence when viewing/editing a feature
  useEffect(() => {
    // Only track presence when panel is open and feature exists
    if (!isOpen || !feature?.id || !userId) {
      setEditingUsers(new Set());
      return;
    }

    // Create a presence channel for this specific feature
    const channelName = `feature-presence:${feature.id}`;
    const presenceChannel = supabase.channel(channelName, {
      config: {
        presence: {
          key: sessionIdRef.current, // Use unique session ID so each browser tab shows separately
        },
      },
    });

    // Helper to extract other users from presence state
    // Note: Since we're using session IDs as keys, we need to collect unique user IDs
    // Multiple sessions from the same user will show as separate presence entries
    // We track the mode (view/edit) for each user, preferring 'edit' if any session is editing
    const extractOtherUsers = () => {
      const state = presenceChannel.presenceState();
      const otherUserIds = new Set();
      const userModes = new Map(); // Map<user_id, 'view' | 'edit'>
      
      Object.entries(state).forEach(([sessionKey, presences]) => {
        presences.forEach((presence) => {
          // Skip our own session
          if (sessionKey !== sessionIdRef.current && presence.user_id) {
            otherUserIds.add(presence.user_id);
            
            // Track mode - prefer 'edit' if any session is editing
            const currentMode = userModes.get(presence.user_id);
            const presenceMode = presence.editing ? 'edit' : (presence.viewing ? 'view' : 'view');
            
            // If already marked as 'edit', keep it; otherwise use the current presence mode
            if (currentMode !== 'edit') {
              userModes.set(presence.user_id, presenceMode);
            }
          }
        });
      });
      
      setEditingUsers(otherUserIds);
      setPresenceUserModes(userModes);
    };

    // Handle presence sync
    presenceChannel.on('presence', { event: 'sync' }, () => {
      extractOtherUsers();
    });

    // Handle user join
    presenceChannel.on('presence', { event: 'join' }, ({ key, newPresences }) => {
      logger.debug('User joined presence:', { key, newPresences });
      extractOtherUsers();
    });

    // Handle user leave
    presenceChannel.on('presence', { event: 'leave' }, ({ key, leftPresences }) => {
      logger.debug('User left presence:', { key, leftPresences });
      extractOtherUsers();
    });

    // Subscribe and track presence
    presenceChannel.subscribe(async (status) => {
      if (status === 'SUBSCRIBED') {
        // Track this user's presence
        await presenceChannel.track({
          user_id: userId,
          user_name: userProfile?.first_name || userProfile?.email || 'Unknown',
          editing: isEditMode || mode === 'edit',
          viewing: mode === 'view',
          entered_at: new Date().toISOString(),
        });
      }
    });

    // Cleanup: Untrack presence when leaving or closing panel
    return () => {
      presenceChannel.untrack();
      presenceChannel.unsubscribe();
    };
  }, [isOpen, feature?.id, userId, isEditMode, mode, userProfile]);

  // Fetch user info (names, avatars) for presence display
  useEffect(() => {
    if (editingUsers.size === 0) {
      setPresenceUserMap(new Map());
      setPresenceUserModes(new Map());
      return;
    }

    const fetchUserInfo = async () => {
      try {
        const userIds = Array.from(editingUsers);
        const { data, error } = await supabase.rpc('get_users_by_ids', {
          p_user_ids: userIds
        });

        if (error) {
          logger.warn('Error fetching presence user info:', error);
          return;
        }

        const userMap = new Map();
        (data || []).forEach(user => {
          userMap.set(user.user_id, {
            name: user.full_name || user.email || 'Unknown',
            email: user.email,
            avatar_url: user.avatar_url,
            first_name: user.first_name,
            last_name: user.last_name
          });
        });
        setPresenceUserMap(userMap);
      } catch (err) {
        logger.warn('Error loading presence user info:', err);
      }
    };

    fetchUserInfo();
  }, [editingUsers]);

  if (!isOpen) return null;

  const panelContent = (
    <>
      {/* Conflict Error Banner */}
      {conflictError && (
        <div className="conflict-error-banner">
          <div className="conflict-error-content">
            <i className="fas fa-exclamation-triangle"></i>
            <span>{conflictError}</span>
            <button
              className="conflict-error-dismiss"
              onClick={() => setConflictError(null)}
              title="Dismiss"
            >
              <i className="fas fa-times"></i>
            </button>
          </div>
        </div>
      )}

      <div className="panel-header">
          <h2>
            {mode === 'add' ? 'Add New Feature' : isEditMode ? 'Edit Feature' : 'Feature Details'}
          </h2>
          <div className="panel-header-actions">
            {/* User Presence Indicator */}
            {isOpen && feature?.id && editingUsers.size > 0 && (
              <div className="presence-indicator-inline">
                <div className="presence-avatars">
                  {Array.from(editingUsers).map(userId => {
                    const userInfo = presenceUserMap.get(userId);
                    const userMode = presenceUserModes.get(userId) || 'view';
                    const displayName = userInfo?.name || userInfo?.email || 'Unknown';
                    const avatarLetter = (userInfo?.first_name?.[0] || userInfo?.last_name?.[0] || userInfo?.email?.[0] || '?').toUpperCase();
                    const isEditing = userMode === 'edit';
                    
                    return (
                      <div 
                        key={userId} 
                        className="presence-avatar-wrapper"
                        onMouseEnter={() => setHoveredPresenceUserId(userId)}
                        onMouseLeave={() => setHoveredPresenceUserId(null)}
                      >
                        <div 
                          className="presence-avatar"
                        >
                          {userInfo?.avatar_url ? (
                            <img 
                              src={userInfo.avatar_url} 
                              alt={displayName}
                              onError={(e) => {
                                e.target.style.display = 'none';
                                if (e.target.nextSibling) {
                                  e.target.nextSibling.style.display = 'flex';
                                }
                              }}
                            />
                          ) : null}
                          <span 
                            className="presence-avatar-initials"
                            style={{ display: userInfo?.avatar_url ? 'none' : 'flex' }}
                          >
                            {avatarLetter}
                          </span>
                          {/* Mode indicator dot - green for editing, blue for viewing */}
                          <div className={`presence-avatar-mode-dot ${isEditing ? 'mode-edit' : 'mode-view'}`}></div>
                        </div>
                        {hoveredPresenceUserId === userId && (
                          <div className="presence-avatar-tooltip">
                            {displayName} {isEditing ? '(editing)' : '(viewing)'}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {((mode === 'view' || mode === 'edit') && !isEditMode && feature && userRole !== 'Guest') && (
              <button className="edit-mode-button" onClick={handleEditClick}>
                <i className="fas fa-edit"></i> Edit
              </button>
            )}
            {!inline && (
              <button
                className="close-button"
                onClick={() => {
                  if (!confirmDiscardChanges()) return;
                  onClose();
                }}
              >
                <i className="fas fa-times"></i>
              </button>
            )}
          </div>
        </div>

        <div className="panel-body">
          {isEditMode || mode === 'add' ? (
            <form onSubmit={handleSubmit} className="panel-form">
              {/* Publicly Visible Section */}
              <div className="form-section">
                <h3 className="form-section-title">Publicly Visible</h3>
                
                <div className="form-group">
                  <label htmlFor="name">
                    Name <span className="required">*</span>
                  </label>
                  <input
                    id="name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className={errors.name ? 'error' : ''}
                    placeholder="Enter feature name"
                    autoComplete="off"
                  />
                  {errors.name && <span className="error-message">{errors.name}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="description">
                    Description <span className="required">*</span>
                    <span className="char-count">{description.length}/250</span>
                  </label>
                  <input
                    id="description"
                    type="text"
                    value={description}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value.length <= 250) {
                        setDescription(value);
                      }
                    }}
                    maxLength={250}
                    className={errors.description ? 'error' : ''}
                    placeholder="Enter feature description (max 250 characters)..."
                    autoComplete="off"
                  />
                  <small className="help-text">
                    Brief description of the feature (required, max 250 characters)
                  </small>
                </div>

                <div className="form-group">
                  <label htmlFor="status">Status</label>
                  <select
                    id="status"
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                  >
                    {STATUS_OPTIONS.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                  <small className="help-text">
                    Current status of this feature
                  </small>
                </div>

                <div className="form-group">
                  <label htmlFor="details">
                    Details
                  </label>
                  <LexicalEditor
                    value={details}
                    onChange={(jsonContent) => setDetails(jsonContent)}
                    placeholder="Enter detailed information about the feature (optional)..."
                  />
                  <small className="help-text">
                    Use the rich text editor to add detailed information about this feature (optional)
                  </small>
                </div>

                <div className="form-group">
                  <label htmlFor="sortOrder">Sort Order {mode === 'add' ? '(optional)' : ''}</label>
                  <select
                    id="sortOrder"
                    value={sortOrder !== null && sortOrder !== undefined ? sortOrder : ''}
                    onChange={(e) => setSortOrder(e.target.value === '' ? null : parseInt(e.target.value))}
                  >
                    {mode === 'add' && <option value="">Auto (add at end)</option>}
                    {getSortOrderOptions().map((option, index) => (
                      <option key={index} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <small className="help-text">
                    {mode === 'add' 
                      ? 'Select the position for this feature among its siblings. "End" will place it after all existing features.'
                      : 'Select the position for this feature among its siblings. Other items will be shifted as needed.'}
                  </small>
                </div>

                <div className="form-group">
                  <label htmlFor="release">Release {mode === 'edit' && feature && (feature.status === 'Live' || feature.status === 'Won\'t do') ? '(read-only)' : ''}</label>
                  <select
                    id="release"
                    value={selectedReleaseId}
                    onChange={(e) => {
                      setSelectedReleaseId(e.target.value);
                    }}
                    disabled={mode === 'edit' && feature && (feature.status === 'Live' || feature.status === 'Won\'t do')}
                    className="release-select"
                  >
                    <option value="">No Release Assigned</option>
                    {availableReleases.map(release => (
                      <option key={release.id} value={release.id}>
                        {release.name} ({release.status})
                      </option>
                    ))}
                  </select>
                  <small className="help-text">
                    Select a release for this feature. {mode === 'edit' && feature && (feature.status === 'Live' || feature.status === 'Won\'t do') ? 'Cannot edit release for Live or Won\'t do features.' : ''}
                  </small>
                </div>

                {mode === 'edit' && feature && (
                  <RelatedInformation 
                    featureId={feature.id}
                    featureGroupId={featureGroupId}
                    isEditMode={true}
                  />
                )}
              </div>

              {/* Internal Only Section */}
              <div className="form-section">
                <h3 className="form-section-title">Internal Only</h3>

                <div className="form-group">
                  <label htmlFor="imageUrl">Image URL</label>
                  <input
                    id="imageUrl"
                    type="url"
                    value={imageUrl}
                    onChange={(e) => setImageUrl(e.target.value)}
                    placeholder="Enter image URL (optional)..."
                    autoComplete="off"
                  />
                  <small className="help-text">
                    URL to an image for this feature (optional)
                  </small>
                </div>

                <div className="form-group parent-feature-group">
                  <label htmlFor="parentId">Parent Feature (optional)</label>
                  <div className="parent-typeahead">
                    <input
                      id="parentId"
                      type="text"
                      value={parentSearchQuery}
                      onChange={handleParentSearchChange}
                      onFocus={() => setShowParentDropdown(true)}
                      onBlur={() => {
                        // Delay hiding to allow click selection
                        setTimeout(() => setShowParentDropdown(false), 150);
                      }}
                      placeholder="Type to search parent feature..."
                      autoComplete="off"
                    />
                    {parentId && (
                      <button
                        type="button"
                        className="clear-parent-button"
                        onClick={handleClearParent}
                        title="Clear parent feature"
                      >
                        <i className="fas fa-times"></i>
                      </button>
                    )}
                    {showParentDropdown && (
                      <div className="parent-dropdown">
                        <div
                          className="parent-option none-option"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={handleClearParent}
                        >
                          None (Top-level feature)
                        </div>
                        {getFilteredParents().length === 0 && (
                          <div className="parent-option no-results">
                            No matching features
                          </div>
                        )}
                        {getFilteredParents().map(parent => (
                          <div
                            key={parent.id}
                            className={`parent-option ${parent.level > 0 ? 'has-indent' : ''}`}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => handleSelectParent(parent)}
                          >
                            {parent.level > 0 && (
                              <span className="parent-level-indicator">{'└'.padStart(parent.level * 2, '  ')}</span>
                            )}
                            <span className="parent-name">{parent.name}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <small className="help-text">
                    Select a parent feature to make this a sub-feature. Type to filter the hierarchy.
                  </small>
                </div>

                <div className="form-group">
                  <label htmlFor="privateFeature" className="checkbox-label">
                    <input
                      id="privateFeature"
                      type="checkbox"
                      checked={privateFeature}
                      onChange={(e) => setPrivateFeature(e.target.checked)}
                    />
                    <span>
                      <i className="fas fa-eye-slash private-feature-icon" style={{ marginRight: '6px' }}></i>
                      Private Feature
                    </span>
                  </label>
                  <small className="help-text">
                    If checked, this feature and all its children will be hidden from Guests in all views.
                  </small>
                </div>

                <div className="form-group">
                  <label>Tags</label>
                  <small className="help-text">
                    New tag domains and tags from the Tags or Admin → Tag Dimensions tabs show up here automatically while this panel is open.
                  </small>
                  <div className="dimensions-tagging-section">
                    {/* Plus menu to add dimensions */}
                    {getAvailableDimensionsForPlus().length > 0 && (
                      <div className="add-dimension-menu-wrapper">
                        <button
                          type="button"
                          className="add-dimension-button"
                          onClick={() => setShowAddDimensionMenu(!showAddDimensionMenu)}
                        >
                          <i className="fas fa-plus"></i> Add Tag Dimension
                        </button>
                        {showAddDimensionMenu && (
                          <div className="add-dimension-dropdown">
                            {getAvailableDimensionsForPlus().map(dim => (
                              <div
                                key={dim.id}
                                className="dimension-option"
                                onClick={() => handleAddDimension(dim)}
                              >
                                <span className="dimension-option-name">{dim.name}</span>
                                <span className="dimension-option-type">
                                  {dim.dimension_type === 'BOOLEAN' ? 'Boolean' : 
                                   dim.dimension_type === 'TRI_STATE' ? 'Yes-No' : 
                                   dim.dimension_type === 'RATING' ? 'Rating' : dim.dimension_type}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Dimension sections */}
                    {Array.from(selectedDimensions).map(dimensionId => {
                      const dimension = visibleDimensions.find(d => d.id === dimensionId);
                      if (!dimension) return null;
                      
                      const tags = dimensionTags.get(dimensionId) || [];
                      const assignments = featureTagAssignments.get(dimensionId) || new Map();
                      const canEdit = canEditDimensionTags(dimension);
                      
                      return (
                        <div key={dimensionId} className="dimension-tag-section">
                          <div className="dimension-tag-section-header">
                            <div className="dimension-tag-section-title">
                              <span className="dimension-name">{dimension.name}</span>
                              <span className="dimension-type-badge">
                                {dimension.dimension_type === 'BOOLEAN' ? 'Boolean' : 
                                 dimension.dimension_type === 'TRI_STATE' ? 'Yes-No' : 
                                 dimension.dimension_type === 'RATING' ? 'Rating' : dimension.dimension_type}
                              </span>
                            </div>
                            <button
                              type="button"
                              className="remove-dimension-button"
                              onClick={() => handleRemoveDimension(dimensionId)}
                              title="Remove dimension"
                            >
                              <i className="fas fa-times"></i>
                            </button>
                          </div>
                          
                          {dimension.user_directions && (
                            <div className="dimension-user-directions">
                              {dimension.user_directions}
                            </div>
                          )}
                          
                          {tags.length === 0 ? (
                            <div className="no-tags-message">No tags available in this dimension.</div>
                          ) : (
                            <div className="dimension-tags-list">
                              {tags.map(tag => {
                                const assignment = assignments.get(tag.id);
                                // For BOOLEAN, check if state is 'TRUE'; for others, check if assignment exists
                                const isSelected = dimension.dimension_type === 'BOOLEAN' 
                                  ? assignment?.state === 'TRUE' 
                                  : !!assignment;
                                const commentKey = `${dimensionId}-${tag.id}`;
                                const isEditingComment = editingComments.get(commentKey);
                                const commentInput = commentInputs.get(commentKey) ?? assignment?.comment ?? '';
                                
                                return (
                                  <div key={tag.id} className="dimension-tag-item-wrapper">
                                    <div className="dimension-tag-item">
                                      {dimension.dimension_type === 'BOOLEAN' && (
                                        <>
                                          <button
                                            type="button"
                                            className={`tag-toggle-button ${isSelected ? 'selected' : ''}`}
                                            onClick={() => handleTagToggle(dimensionId, tag.id, dimension.dimension_type)}
                                            disabled={!canEdit}
                                          >
                                            {tag.name}
                                          </button>
                                          {isSelected && (
                                            <span className={`tag-comment-icon ${assignment?.comment ? 'has-comment' : 'no-comment'}`} title={assignment?.comment || 'No comment'}>
                                              <i className={`fas fa-comment${assignment?.comment ? '' : '-dots'}`}></i>
                                            </span>
                                          )}
                                        </>
                                      )}
                                    
                                      {dimension.dimension_type === 'TRI_STATE' && (
                                        <>
                                          <span className="tag-name-label">{tag.name}</span>
                                          <div className="tri-state-controls">
                                            {['YES', 'NO', 'UNSET'].map(state => (
                                              <button
                                                key={state}
                                                type="button"
                                                className={`tri-state-button ${assignment?.state === state ? 'active' : ''}`}
                                                onClick={() => {
                                                  if (assignment?.state === state) {
                                                    handleTagToggle(dimensionId, tag.id, dimension.dimension_type);
                                                  } else {
                                                    if (!isSelected) {
                                                      handleTagToggle(dimensionId, tag.id, dimension.dimension_type);
                                                    }
                                                    handleTagAssignmentUpdate(dimensionId, tag.id, { state });
                                                  }
                                                }}
                                                disabled={!canEdit}
                                              >
                                                {state === 'YES' ? 'Yes' : state === 'NO' ? 'No' : '—'}
                                              </button>
                                            ))}
                                          </div>
                                          {isSelected && (
                                            <span className={`tag-comment-icon ${assignment?.comment ? 'has-comment' : 'no-comment'}`} title={assignment?.comment || 'No comment'}>
                                              <i className={`fas fa-comment${assignment?.comment ? '' : '-dots'}`}></i>
                                            </span>
                                          )}
                                        </>
                                      )}
                                    
                                      {dimension.dimension_type === 'RATING' && (
                                        <>
                                          <span className="tag-name-label">{tag.name}</span>
                                          <div className="rating-controls">
                                            {[1, 2, 3, 4, 5].map(rating => (
                                              <button
                                                key={rating}
                                                type="button"
                                                className={`rating-button ${assignment?.rating === rating ? 'active' : ''}`}
                                                onClick={() => {
                                                  if (assignment?.rating === rating) {
                                                    handleTagToggle(dimensionId, tag.id, dimension.dimension_type);
                                                  } else {
                                                    if (!isSelected) {
                                                      handleTagToggle(dimensionId, tag.id, dimension.dimension_type);
                                                    }
                                                    handleTagAssignmentUpdate(dimensionId, tag.id, { rating });
                                                  }
                                                }}
                                                disabled={!canEdit}
                                              >
                                                {rating}
                                              </button>
                                            ))}
                                            <button
                                              type="button"
                                              className={`rating-button rating-clear-button ${!assignment?.rating ? 'active' : ''}`}
                                              onClick={() => {
                                                if (!isSelected) {
                                                  handleTagToggle(dimensionId, tag.id, dimension.dimension_type);
                                                }
                                                handleTagAssignmentUpdate(dimensionId, tag.id, { rating: null });
                                              }}
                                              disabled={!canEdit}
                                              title="Clear rating"
                                            >
                                              <i className="fas fa-times"></i>
                                            </button>
                                          </div>
                                          {isSelected && (
                                            <span className={`tag-comment-icon ${assignment?.comment ? 'has-comment' : 'no-comment'}`} title={assignment?.comment || 'No comment'}>
                                              <i className={`fas fa-comment${assignment?.comment ? '' : '-dots'}`}></i>
                                            </span>
                                          )}
                                        </>
                                      )}
                                    </div>
                                    
                                    {/* Comment section - only show if tag is selected */}
                                    {isSelected && (
                                      <div className="tag-comment-section">
                                        {isEditingComment && canEdit ? (
                                          <div className="tag-comment-edit-form">
                                            <textarea
                                              className="tag-comment-textarea"
                                              value={commentInput}
                                              onChange={(e) => {
                                                const key = `${dimensionId}-${tag.id}`;
                                                setCommentInputs(prev => new Map(prev).set(key, e.target.value));
                                              }}
                                              placeholder="Enter comment..."
                                              rows={3}
                                            />
                                            <div className="tag-comment-form-actions">
                                              <button
                                                type="button"
                                                className="tag-comment-save-button"
                                                onClick={() => handleSaveComment(dimensionId, tag.id)}
                                              >
                                                Save
                                              </button>
                                              <button
                                                type="button"
                                                className="tag-comment-cancel-button"
                                                onClick={() => handleCancelEditComment(dimensionId, tag.id)}
                                              >
                                                Cancel
                                              </button>
                                            </div>
                                          </div>
                                        ) : assignment?.comment ? (
                                          <div className="tag-comment-display">
                                            <div className="tag-comment-text">{assignment.comment}</div>
                                            {canEdit && (
                                              <div className="tag-comment-actions">
                                                <button
                                                  type="button"
                                                  className="tag-comment-edit-button"
                                                  onClick={() => handleStartEditComment(dimensionId, tag.id, assignment.comment)}
                                                >
                                                  Edit
                                                </button>
                                                <button
                                                  type="button"
                                                  className="tag-comment-delete-button"
                                                  onClick={() => handleDeleteComment(dimensionId, tag.id)}
                                                >
                                                  Delete
                                                </button>
                                              </div>
                                            )}
                                          </div>
                                        ) : canEdit ? (
                                          <button
                                            type="button"
                                            className="tag-comment-add-button"
                                            onClick={() => handleStartEditComment(dimensionId, tag.id, '')}
                                          >
                                            <i className="fas fa-comment-dots"></i> Add comment
                                          </button>
                                        ) : null}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                    
                    {selectedDimensions.size === 0 && (
                      <div className="no-dimensions-message">
                        Click "Add Tag Dimension" to start tagging this feature.
                      </div>
                    )}
                  </div>
                  <small className="help-text">
                    Add dimensions to categorize and rate this feature
                  </small>
                </div>

                <div className="form-group">
                  <label htmlFor="aiPrompt">AI Prompt</label>
                  <textarea
                    id="aiPrompt"
                    value={aiPrompt}
                    onChange={(e) => setAiPrompt(e.target.value)}
                    placeholder="Enter AI prompt used to generate this feature (optional)..."
                    rows={4}
                    className="ai-prompt-textarea"
                  />
                  <small className="help-text">
                    The AI prompt that was used to create this feature (optional, for reference)
                  </small>
                </div>

                {feature?.id && (
                  <div className="form-group">
                    <button
                      type="button"
                      className="improve-prompt-button"
                      onClick={async () => {
                        // If in edit mode, always save first to ensure latest data is saved
                        if (mode === 'edit') {
                          try {
                            // Validate and build the feature data to save
                            if (!validate()) {
                              alert('Please fix validation errors before opening Improve Feature.');
                              return;
                            }
                            
                            // Show loading spinner immediately after validation passes
                            if (onImprovePromptStart) {
                              onImprovePromptStart();
                            }
                            
                            const { featureData, releaseIds } = buildFeaturePayload();
                            const tagAssignments = getTagAssignmentsForSave();
                            
                            // Save the feature first
                            if (onSave) {
                              await onSave(featureData, tagAssignments, releaseIds);
                              // After save, reset unsaved changes flag
                              setInitialDetails(featureData.details || '');
                              setInitialAiPrompt(featureData.ai_prompt || '');
                              setHasUnsavedChanges(false);
                              
                              // Reload feature tags
                              if (feature?.id) {
                                await loadFeatureTags();
                              }
                              
                              // Build updated feature object with all saved data
                              const updatedFeature = {
                                ...feature,
                                name: featureData.name,
                                description: featureData.description,
                                details: featureData.details,
                                status: featureData.status,
                                parent_id: featureData.parent_id,
                                sort_order: featureData.sort_order,
                                image_url: featureData.image_url,
                                ai_prompt: featureData.ai_prompt,
                                private_feature: featureData.private_feature
                              };
                              
                              // Wait a moment for parent state to update, then open Improve Feature
                              // Pass the updated feature with all saved changes
                              setTimeout(() => {
                                if (onImprovePrompt) {
                                  onImprovePrompt(updatedFeature);
                                }
                              }, 200);
                            } else {
                              // No onSave handler, just open Improve Feature with current form data
                              const currentFeatureData = {
                                ...feature,
                                name: name,
                                description: description,
                                details: details,
                                status: status,
                                parent_id: parentId || null,
                                sort_order: sortOrder !== null && sortOrder !== undefined ? parseInt(sortOrder) : undefined,
                                image_url: imageUrl || null,
                                ai_prompt: aiPrompt || null,
                                private_feature: privateFeature
                              };
                              if (onImprovePrompt) {
                                onImprovePrompt(currentFeatureData);
                              }
                            }
                          } catch (err) {
                            logger.error('Error saving feature before opening Improve Feature:', err);
                            // Don't open Improve Feature if save failed - user should fix errors first
                            alert('Please save your changes before opening Improve Feature. There were errors saving the feature.');
                            // Hide loading spinner if save failed by closing the improve prompt view
                            // This will be handled by the error state in App.js
                          }
                        } else {
                          // Not in edit mode, show loading spinner and open Improve Feature
                          if (onImprovePromptStart) {
                            onImprovePromptStart();
                          }
                          if (onImprovePrompt) {
                            onImprovePrompt(feature);
                          }
                        }
                      }}
                    >
                      <i className="fas fa-magic"></i> improve feature with AI
                    </button>
                  </div>
                )}
              </div>

              <div className="panel-actions">
                {mode === 'edit' && (
                  <button type="button" className="cancel-button" onClick={handleCancel} disabled={saveBusy}>
                    Cancel to View
                  </button>
                )}
                {mode === 'add' && (
                  <button type="button" className="cancel-button" onClick={onClose} disabled={saveBusy}>
                    Cancel
                  </button>
                )}

                <div className="panel-actions-spacer" />

                <div className="other-options-wrapper">
                  <button
                    type="button"
                    className="other-options-button"
                    onClick={() => setShowOtherOptions((prev) => !prev)}
                    disabled={saveBusy}
                  >
                    Other Options ▾
                  </button>
                  {showOtherOptions && (
                    <div className="other-options-menu">
                      <button
                        type="button"
                        className="other-options-item"
                        disabled={saveBusy}
                        onClick={async () => {
                          try {
                            await performSave(false);
                          } finally {
                            setShowOtherOptions(false);
                          }
                        }}
                      >
                        {savingState === 'continue' ? (
                          <>
                            <i className="fas fa-spinner fa-spin save-action-spinner" aria-hidden />
                            Saving…
                          </>
                        ) : (
                          'Save'
                        )}
                      </button>
                      <button
                        type="button"
                        className="other-options-item"
                        disabled={saveBusy}
                        onClick={async () => {
                          try {
                            await performSaveAndCopy();
                          } finally {
                            setShowOtherOptions(false);
                          }
                        }}
                      >
                        {savingState === 'copy' ? (
                          <>
                            <i className="fas fa-spinner fa-spin save-action-spinner" aria-hidden />
                            Saving…
                          </>
                        ) : (
                          mode === 'add' ? 'Save and Duplicate' : 'Duplicate'
                        )}
                      </button>
                    </div>
                  )}
                </div>

                <button
                  type="submit"
                  className="save-button"
                  disabled={saveBusy}
                >
                  {savingState === 'continue' ? (
                    <>
                      <i className="fas fa-spinner fa-spin save-action-spinner" aria-hidden />
                      Saving…
                    </>
                  ) : (
                    'Save'
                  )}
                </button>

                <button
                  type="button"
                  className="save-close-button"
                  disabled={saveBusy}
                  onClick={(e) => handleSubmit(e, true)}
                >
                  {savingState === 'close' ? (
                    <>
                      <i className="fas fa-spinner fa-spin save-action-spinner" aria-hidden />
                      Saving…
                    </>
                  ) : (
                    'Save and Close'
                  )}
                </button>
              </div>            </form>
          ) : (
            <div className="feature-view">
              {/* Publicly Visible Section */}
              <div className="form-section">
                {userRole !== 'Guest' && (
                  <h3 className="form-section-title">Publicly Visible</h3>
                )}
                
                <div className="view-section">
                  <label>Name</label>
                  <div className="view-value">{feature?.name || 'N/A'}</div>
                </div>

                <div className="view-section">
                  <label>Description</label>
                  <div className="view-value view-description">
                    {feature?.description || 'N/A'}
                  </div>
                </div>

                <div className="view-section">
                  <label>Status</label>
                  <div className="view-value">
                    <span className={`status-pill ${getStatusClass(feature?.status || 'No Status')}`}>
                      {feature?.status || 'No Status'}
                    </span>
                  </div>
                </div>

                {feature?.details && (
                  <div className="view-section">
                    <label>Details</label>
                    <div className="view-value view-details">
                      <LexicalViewer content={feature.details} />
                    </div>
                  </div>
                )}

                <div className="view-section">
                  <label>Release</label>
                  <div className="view-value">
                    {featureReleases.length > 0 ? (
                      <span className="release-badge">
                        {featureReleases[0].name}
                      </span>
                    ) : (
                      <span className="no-releases">No Release Assigned</span>
                    )}
                  </div>
                </div>

                {feature && (
                  <div style={{ marginTop: '24px' }}>
                    <RelatedInformation 
                      featureId={feature.id}
                      featureGroupId={featureGroupId}
                      isEditMode={false}
                    />
                  </div>
                )}
              </div>

              {/* Internal Only Section */}
              {userRole !== 'Guest' && (
                <div className="form-section">
                  <h3 className="form-section-title">Internal Only</h3>

                <div className="view-section">
                  <label>ID</label>
                  <div className="view-value view-id-container">
                    <span className="view-id">{feature?.id || 'N/A'}</span>
                    {feature?.id && (
                      <button
                        type="button"
                        className="copy-url-button"
                        onClick={() => {
                          const url = `${window.location.origin}/feature/${feature.id}`;
                          navigator.clipboard.writeText(url).then(() => {
                            // Show temporary feedback
                            const button = document.querySelector('.copy-url-button');
                            if (button) {
                              const originalText = button.innerHTML;
                              button.innerHTML = '<i class="fas fa-check"></i>';
                              button.style.color = '#4CAF50';
                              setTimeout(() => {
                                button.innerHTML = originalText;
                                button.style.color = '';
                              }, 2000);
                            }
                          }).catch(err => {
                            logger.error('Failed to copy URL:', err);
                          });
                        }}
                        title="Copy feature URL to clipboard"
                      >
                        <i className="fas fa-copy"></i>
                      </button>
                    )}
                  </div>
                </div>

                <div className="view-section">
                  <label>Parent Feature</label>
                  <div className="view-value">
                    {feature?.parent_id ? getParentName(feature.parent_id) : 'None (Top-level feature)'}
                  </div>
                </div>

                <div className="view-section">
                  <label>Private Feature</label>
                  <div className="view-value">
                    {feature?.private_feature ? (
                      <span className="private-feature-indicator">
                        <i className="fas fa-eye-slash"></i> Yes (hidden from Guests)
                      </span>
                    ) : (
                      <span>No</span>
                    )}
                  </div>
                </div>

                <div className="view-section">
                  <label>Tags</label>
                  <div className="view-value">
                    {selectedDimensions.size > 0 ? (
                      <div className="dimensions-display-view">
                        {Array.from(selectedDimensions).map(dimensionId => {
                          const dimension = visibleDimensions.find(d => d.id === dimensionId);
                          if (!dimension) return null;
                          
                          const tags = dimensionTags.get(dimensionId) || [];
                          const assignments = featureTagAssignments.get(dimensionId) || new Map();
                          // For BOOLEAN, show tags with TRUE state; for others, show all assigned tags
                          const assignedTags = dimension.dimension_type === 'BOOLEAN'
                            ? tags.filter(tag => {
                                const assignment = assignments.get(tag.id);
                                return assignment && assignment.state === 'TRUE';
                              })
                            : tags.filter(tag => assignments.has(tag.id));
                          
                          if (assignedTags.length === 0) return null;
                          
                          return (
                            <div key={dimensionId} className="dimension-display-group">
                              <div className="dimension-display-header">
                                <span className="dimension-display-name">{dimension.name}</span>
                                <span className="dimension-display-type">
                                  {dimension.dimension_type === 'BOOLEAN' ? 'Boolean' : 
                                   dimension.dimension_type === 'TRI_STATE' ? 'Yes-No' : 
                                   dimension.dimension_type === 'RATING' ? 'Rating' : dimension.dimension_type}
                                </span>
                              </div>
                              <div className="dimension-tags-display">
                                {assignedTags.map(tag => {
                                  const assignment = assignments.get(tag.id);
                                  return (
                                    <div key={tag.id} className="dimension-tag-display-item">
                                      <span className="tag-name">{tag.name}</span>
                                      {dimension.dimension_type === 'BOOLEAN' && assignment?.state === 'FALSE' && (
                                        <span className="tag-state-badge state-false">No</span>
                                      )}
                                      {dimension.dimension_type === 'TRI_STATE' && assignment?.state && (
                                        <span className={`tag-state-badge state-${assignment.state.toLowerCase()}`}>
                                          {assignment.state === 'YES' ? 'Yes' : assignment.state === 'NO' ? 'No' : 'Unset'}
                                        </span>
                                      )}
                                      {dimension.dimension_type === 'RATING' && assignment?.rating && (
                                        <span className="tag-rating-badge">⭐ {assignment.rating}</span>
                                      )}
                                      <span className={`tag-comment-icon-view ${assignment?.comment ? 'has-comment' : 'no-comment'}`} title={assignment?.comment || 'No comment'}>
                                        <i className={`fas fa-comment${assignment?.comment ? '' : '-dots'}`}></i>
                                      </span>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <span className="no-tags">No tags</span>
                    )}
                  </div>
                </div>

                <div className="view-section">
                  <label>Created</label>
                  <div className="view-value">
                    {feature?.created_at 
                      ? new Date(feature.created_at).toLocaleString() 
                      : 'N/A'}
                  </div>
                </div>

                {feature?.updated_at && (
                  <div className="view-section">
                    <label>Last Updated</label>
                    <div className="view-value">
                      {new Date(feature.updated_at).toLocaleString()}
                    </div>
                  </div>
                )}

                <div className="view-section">
                  <label>AI Prompt</label>
                  <div className="view-value view-ai-prompt-container">
                    <div className="view-ai-prompt-text">
                      {feature?.ai_prompt && feature.ai_prompt.trim() ? feature.ai_prompt : 'No data'}
                    </div>
                    {feature?.ai_prompt && feature.ai_prompt.trim() && (
                      <button
                        type="button"
                        className="copy-ai-prompt-button"
                        onClick={() => {
                          navigator.clipboard.writeText(feature.ai_prompt).then(() => {
                            // Show temporary feedback
                            const button = document.querySelector('.copy-ai-prompt-button');
                            if (button) {
                              const originalHTML = button.innerHTML;
                              button.innerHTML = '<i class="fas fa-check"></i>';
                              button.style.color = '#4CAF50';
                              setTimeout(() => {
                                button.innerHTML = originalHTML;
                                button.style.color = '';
                              }, 2000);
                            }
                          }).catch(err => {
                            logger.error('Failed to copy AI prompt:', err);
                          });
                        }}
                        title="Copy AI prompt to clipboard"
                      >
                        <i className="fas fa-copy"></i>
                      </button>
                    )}
                  </div>
                </div>
              </div>
              )}
            </div>
          )}
        </div>
    </>
  );

  if (inline) {
    // Inline mode: render just the content without overlay or slide-out behavior
    return (
      <div className="feature-panel-inline" ref={panelContentRef}>
        <div className="panel-content-inline">
          {panelContent}
        </div>
      </div>
    );
  }

  // Normal slide-out panel mode
  return (
    <div className={`feature-panel ${isOpen ? 'open' : ''}`}>
      <div className="panel-content" ref={panelContentRef}>
        {panelContent}
      </div>
      <div className="panel-overlay" onClick={onClose}></div>
    </div>
  );
};

export default FeaturePanel;
